import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/solid";
import produce from "immer";
import { orderBy } from "lodash";
import moment from "moment";
import { stringifyUrl } from "query-string";
import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { useCommissionReports } from ".";
import CenteredLoading from "../../components/CenteredLoading";
import Copy from "../../components/Copy";
import { getLabelColorStyle } from "../../components/labels/utils";
import StatusDot from "../../components/StatusDot";
import { avatarsApi } from "../../utils/constants";
import { formatDateToLocale } from "../../utils/dates";
import {
  formatHotspotName,
  formatHotspotPlace
} from "../../utils/hotspotUtils";
import { truncateText } from "../../utils/text";
import {
  CommissionReport,
  CommissionSubset,
  CommissionType,
  CommissionValues,
  ContactCommission
} from "../../utils/types";
import useCurrency, { CurrencyKey } from "../../utils/useCurrency";
import { useHntMarket } from "../../utils/useHntMarket";
import { useAppearance } from "../appearance";
import { useCommissionTemplates } from "../commissionTemplates";
import { useHotspots } from "../hotspots";
import { useLocations } from "../locations";

interface Props {
  commissionReport: CommissionReport;
  commissions: ContactCommission[];
  closeSidebar: (isCloseAll: boolean) => void;
}

const ContactCommissionsList: React.FC<Props> = ({
  commissionReport,
  commissions,
  closeSidebar
}) => {
  const { hotspotsMap } = useHotspots();
  const { locations } = useLocations();
  const { isDark } = useAppearance();
  const { currencyDisplay } = useCurrency();
  const { displayInCurrency } = useHntMarket();
  const { fetchCommissionReportDetails } = useCommissionReports();
  const { getCommissionValueLabel, getCommissionTemplateLabel } =
    useCommissionTemplates();
  const [showAllRewardsMap, setShowAllRewardsMap] = useState<{
    [commissionUuid: string]: boolean;
  }>({});
  const [commissionValues, setCommissionValues] = useState<{
    [commissionUuid: string]: CommissionValues[];
  }>({});
  const [loadingCommissionValues, setLoadingCommissionValues] = useState<{
    [commissionUuid: string]: boolean;
  }>({});

  const showAllRewards = useCallback(
    async ({ install, commission }: ContactCommission) => {
      const commissionReportKey = `${commission.uuid}-${commissionReport?.start_date}-${commissionReport?.end_date}`;

      if (
        !commissionReport ||
        !commission.uuid ||
        commissionValues[commissionReportKey]
      ) {
        return;
      }

      setLoadingCommissionValues((prevState) => ({
        ...prevState,
        [commissionReportKey]: true
      }));

      const response = await fetchCommissionReportDetails({
        hotspotId: install.hotspot.uuid,
        installed_at: install.installed_at || null,
        uninstalled_at: install.uninstalled_at || null,
        start_date: commissionReport.start_date,
        end_date: commissionReport.end_date,
        currency: commission.currency,
        type: commission.type,
        cron_expression: commission.cron_expression || null,
        value: commission.value,
        timezone: commissionReport.timezone,
        commission_start_date: commission.start_date,
        commission_end_date: commission.end_date || null
      });

      if (response.status) {
        setCommissionValues((prevState) => ({
          ...prevState,
          [commissionReportKey]: response.data
        }));
      }

      setLoadingCommissionValues((prevState) => ({
        ...prevState,
        [commissionReportKey]: false
      }));
    },
    [commissionReport, commissionValues, fetchCommissionReportDetails]
  );

  return (
    <>
      {commissions.length === 1 ? (
        <h3 className="text-sm font-medium text-gray-500 flex items-center justify-between">
          <span>Commission</span>
          <span>Amount</span>
        </h3>
      ) : (
        <h3 className="text-sm font-medium text-gray-500 flex items-center justify-between">
          <span>Commissions ({commissions.length})</span>
          <span>Amounts</span>
        </h3>
      )}
      <dl className="mt-2 border-t border-gray-200 divide-y divide-gray-200">
        {commissions.map((contactCommission) => {
          const { totalCommissionValue, commission, install } =
            contactCommission;
          const hotspot = hotspotsMap[install.hotspot.uuid];
          const location = locations[install.location.uuid];
          const commissionReportKey = `${commission.uuid}-${commissionReport?.start_date}-${commissionReport?.end_date}`;
          const commissionValueDecimals = [
            CommissionType.oneTimeFixed,
            CommissionType.recurringFixed
          ].includes(commission.type)
            ? 2
            : 6;

          return (
            <div key={commission.uuid}>
              <div className="py-3 flex items-start justify-between text-sm font-medium">
                <dt className="flex-1">
                  <Link
                    className="relative flex items-center space-x-3 group text-sm font-medium text-gray-900 dark:text-white"
                    to={`/installs/${install.uuid}/info`}
                    onClick={() => {
                      closeSidebar(true);
                    }}
                  >
                    <div className="flex-shrink-0">
                      <span className="inline-block relative">
                        <img
                          className="h-10 w-10 rounded-full"
                          src={stringifyUrl({
                            url: `${avatarsApi}/bottts/${install.hotspot.uuid}.svg`,
                            query: { w: 40, h: 40 }
                          })}
                          alt="Hotspot avatar"
                        />
                        {hotspot && (
                          <StatusDot
                            hotspot={hotspot}
                            withoutCustomRewardScaling
                            className="absolute top-0 right-0 block"
                          />
                        )}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0 focus:outline-none">
                      <div>
                        <span className="absolute inset-0" aria-hidden="true" />
                        <p className="text-sm font-medium text-gray-900 dark:text-white group-hover:underline">
                          {hotspot
                            ? formatHotspotName(hotspot)
                            : truncateText(install.hotspot.uuid, 20)}
                        </p>
                        <p className="text-sm font-normal text-gray-500 truncate dark:text-gray-400">
                          {formatHotspotPlace(location?.name)}
                        </p>
                        {install.installed_at && (
                          <p className="text-xs font-light text-gray-500 truncate dark:text-gray-400">
                            Hotspot installed{" "}
                            {install.uninstalled_at
                              ? `from ${formatDateToLocale(
                                  install.installed_at
                                )} to ${formatDateToLocale(
                                  install.uninstalled_at
                                )}`
                              : `on ${formatDateToLocale(
                                  install.installed_at
                                )}`}
                          </p>
                        )}
                        <p className="text-xs font-light text-gray-500 truncate dark:text-gray-400">
                          {getCommissionPeriodText(commission)}
                        </p>
                      </div>
                      <div className="flex items-center flex-wrap">
                        <div
                          className="inline-flex items-center rounded-full font-medium px-3 text-sm mr-2 mt-2"
                          style={getLabelColorStyle("#7057ff", isDark)}
                        >
                          {getCommissionTemplateLabel(commission)}
                        </div>
                        <div
                          className="inline-flex items-center rounded-full font-medium px-3 text-sm mt-2"
                          style={getLabelColorStyle("#d73aaa", isDark)}
                        >
                          {getCommissionValueLabel(commission)}
                        </div>
                      </div>
                    </div>
                  </Link>
                </dt>
                <dd className="text-md font-semibold text-gray-900 dark:text-white">
                  {commissionReport.currency === CurrencyKey.hnt
                    ? displayInCurrency(totalCommissionValue, {
                        withHntSuffix: true,
                        needsDivision: true,
                        minimumFractionDigits: 3,
                        maximumFractionDigits: 3
                      })
                    : currencyDisplay(
                        commissionReport.currency,
                        totalCommissionValue
                      )}
                </dd>
              </div>

              <div
                className="hover:underline font-medium cursor-pointer text-sm text-right mb-2"
                onClick={() => {
                  setShowAllRewardsMap((prevMap) =>
                    produce(prevMap, (draft) => {
                      draft[commissionReportKey] = !draft[commissionReportKey];
                    })
                  );

                  showAllRewards(contactCommission);
                }}
              >
                {showAllRewardsMap[commissionReportKey] ? (
                  <>
                    Hide all commissions{" "}
                    <ChevronUpIcon className="h-4 w-4 inline" />
                  </>
                ) : (
                  <>
                    Show all commissions{" "}
                    <ChevronDownIcon className="h-4 w-4 inline" />
                  </>
                )}
              </div>

              {showAllRewardsMap[commissionReportKey] && commission.uuid && (
                <div className="ml-12 mb-4 border-t border-none divide-y divide-gray-500">
                  {loadingCommissionValues[commissionReportKey] ? (
                    <CenteredLoading className="h-5 w-5 text-gray-400 mx-auto mt-6" />
                  ) : (
                    commissionValues[commissionReportKey] &&
                    orderBy(
                      commissionValues[commissionReportKey],
                      ["timestamp"],
                      ["asc"]
                    ).map(({ timestamp, currency, amount, tx_hash }) => (
                      <div
                        key={timestamp}
                        className="py-3 flex items-start justify-between text-sm font-medium"
                      >
                        <dt>
                          {moment.unix(timestamp).utc().format("MMM Do, HH:mm")}

                          {tx_hash && (
                            <Copy
                              text={tx_hash}
                              label="transaction hash"
                              className="inline ml-1 h-4 w-4"
                            />
                          )}
                        </dt>
                        <dd className="text-md font-semibold text-gray-900 dark:text-white">
                          {currency === CurrencyKey.hnt
                            ? displayInCurrency(amount, {
                                withHntSuffix: false,
                                needsDivision: true,
                                minimumFractionDigits: commissionValueDecimals,
                                maximumFractionDigits: commissionValueDecimals
                              })
                            : currencyDisplay(currency, amount, {
                                minimumFractionDigits: commissionValueDecimals,
                                maximumFractionDigits: commissionValueDecimals
                              })}
                        </dd>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}
      </dl>
    </>
  );
};

export default ContactCommissionsList;

//
// Utils
//

const getCommissionPeriodText = (commission: CommissionSubset) => {
  if (commission.type === CommissionType.oneTimeFixed) {
    return `Commission on ${formatDateToLocale(commission.start_date)}`;
  }

  const text = `Commissions from ${formatDateToLocale(commission.start_date)}`;

  return commission.end_date
    ? `${text} to ${formatDateToLocale(commission.end_date)}`
    : text;
};
